import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  CalendarX,
  CalendarX2,
  Check,
  Clock,
  Loader2,
  MessageSquare,
  SquarePen,
  Trash2,
} from "lucide-react";

import { JobOfferStatus, WageFrequency } from "@api/shared-runtime/enums";
import { JOB_OFFER_EXPIRED_ERROR_MESSAGE } from "@api/shared-runtime/job-offer-errors";
import React, { useState } from "react";
import {
  MapPin,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  X,
  DollarSign,
} from "lucide-react";
import { RenderHtml } from "@/components/RenderHtml";
import { wageFrequencyToEnding } from "../utils/wage-frequency-to-ending";
import { formatAddress } from "../utils/format-address";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMutation } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC } from "@/trpc";
import { format, parseISO } from "date-fns";
import { ProviderNameOpenModal } from "@/features/profile/components/ProviderNameOpenModal";
import { ScheduleSummary } from "../components/schedule-summary";
import { ProposalListItem } from "../components/proposal-list-item";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export interface JobOfferCardProps {
  /** When true, removes card border/shadow for use inside dialogs */
  inDialog?: boolean;
  /** Callback to close the dialog when inDialog is true */
  onClose?: () => void;
  /** When true, uses a compact white design without colored header (for embedding in colored containers) */
  compact?: boolean;
  /** When true, hides cancel/action buttons from the card (actions handled externally) */
  hideActions?: boolean;
  jobOffer: {
    id?: string;
    organizationBusinessUnit?: {
      id: string;
      name: string;
    } | null;
    facility: {
      id: string;
      name: string;
      address1: string | null;
      address2?: string | null;
      city: string | null;
      state: string | null;
      zipCode: string | null;
    } | null;
    // Wage rates (10 combinations: 5 base types × 2 overtime states)
    regularWage?: string | null;
    regularOvertimeWage?: string | null;
    weekendWage?: string | null;
    weekendOvertimeWage?: string | null;
    onCallWage?: string | null;
    onCallOvertimeWage?: string | null;
    onCallCallbackWage?: string | null;
    onCallCallbackOvertimeWage?: string | null;
    holidayWage?: string | null;
    holidayOvertimeWage?: string | null;
    wageFrequencyId: WageFrequency;
    description?: string | null;
    title?: string | null;
    schedules?: {
      id: string;
      startsAt: Date | string;
      durationMinutes: number;
      rrule?: string | null;
      rruleEndsAt?: Date | string | null;
    }[];
    shifts?: {
      id: string;
      startsAt: Date | string;
      endsAt: Date | string;
      status: string;
    }[];
    providerPools?:
      | {
          id: string;
          name: string;
        }[]
      | null;
    providerUsers?: {
      id: string;
      name: string;
    }[];
    decision?: {
      organizationDecision: boolean | null;
      organizationDecisionAt: string | null;
      providerDecision: boolean | null;
      providerDecisionAt: string | null;
      providerCanceledAt: string | null;
      isMe: boolean;
    } | null;
    decisionUser?: {
      id: string;
      firstName: string | null;
      lastName: string | null;
    } | null;
    accepted?: boolean;
    canceledByOrganizationBusinessUnitAt?: string | null;
    status?: JobOfferStatus;
    expiresAt?: Date | string | null;
    sharedPoolMembers?: {
      id: string;
    }[];
    sharedUsers?: {
      id: string;
    }[];
    proposals?: Array<{
      id: string;
      status: string;
      initiatedAt: Date | string;
      initiatedByUserId: string;
      initiatedByRole: string;
      expiresAt: Date | string | null;
      scheduleCount: number;
      shiftCancelCount: number;
    }>;
  };
  className?: string;
}

export function JobOfferCard({
  jobOffer,
  className,
  inDialog,
  onClose,
  compact,
  hideActions,
}: JobOfferCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelShiftDialog, setShowCancelShiftDialog] = useState(false);
  const [shiftToCancel, setShiftToCancel] = useState<string | null>(null);
  const [cancelShiftNote, setCancelShiftNote] = useState("");
  const [cancelOfferNote, setCancelOfferNote] = useState("");

  const wageFrequency = jobOffer?.wageFrequencyId
    ? wageFrequencyToEnding[jobOffer?.wageFrequencyId]
    : "";
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const trpc = useTRPC();

  const { mutateAsync: decide } = useMutation(
    trpc.jobOffer.decide.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListOpen.queryOptions(),
          ),
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListAccepted.queryOptions(),
          ),
          jobOffer.id
            ? queryClient.invalidateQueries(
                trpc.jobOffer.getForCard.queryOptions({
                  id: jobOffer.id,
                }),
              )
            : Promise.resolve(),
        ]);
      },
      onError: async (err) => {
        const isExpiredError =
          err instanceof TRPCClientError &&
          err.data?.code === "FORBIDDEN" &&
          err.message === JOB_OFFER_EXPIRED_ERROR_MESSAGE;

        if (isExpiredError) {
          toast.error("This offer just expired and is no longer available.");
        } else {
          toast.error(err.message || "Could not respond to this offer.");
        }

        await Promise.all([
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListOpen.queryOptions(),
          ),
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListExpired.queryOptions(),
          ),
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListCancelled.queryOptions(),
          ),
          jobOffer.id
            ? queryClient.invalidateQueries(
                trpc.jobOffer.getForCard.queryOptions({
                  id: jobOffer.id,
                }),
              )
            : Promise.resolve(),
        ]);
      },
    }),
  );

  const { mutateAsync: providerCancel } = useMutation(
    trpc.jobOffer.providerCancel.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListOpen.queryOptions(),
          ),
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListAccepted.queryOptions(),
          ),
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListCancelled.queryOptions(),
          ),
          jobOffer.id
            ? queryClient.invalidateQueries(
                trpc.jobOffer.getForCard.queryOptions({
                  id: jobOffer.id,
                }),
              )
            : Promise.resolve(),
        ]);
      },
    }),
  );

  const { mutateAsync: providerCancelShifts } = useMutation(
    trpc.jobOffer.providerCancelShifts.mutationOptions({
      onSuccess: async () => {
        setShowCancelShiftDialog(false);
        setShiftToCancel(null);
        setCancelShiftNote("");
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.jobOffer.providerListAccepted.queryOptions(),
          ),
          jobOffer.id
            ? queryClient.invalidateQueries(
                trpc.jobOffer.getForCard.queryOptions({
                  id: jobOffer.id,
                }),
              )
            : Promise.resolve(),
        ]);
      },
    }),
  );

  const handleAcceptClick = () => {
    setShowConfirmDialog(true);
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!jobOffer.id) {
      return;
    }
    setShowCancelDialog(false);
    setCancelLoading(true);
    try {
      await providerCancel({
        jobOfferId: jobOffer.id,
        note: cancelOfferNote.trim(),
      });
      setCancelOfferNote("");
    } finally {
      setCancelLoading(false);
    }
  };
  const handleConfirmAccept = async () => {
    if (!jobOffer.id) {
      return;
    }
    setShowConfirmDialog(false);
    setAcceptLoading(true);
    try {
      await decide({
        id: jobOffer.id,
        decision: true,
      });
    } catch {
      // onError already surfaced the toast and invalidated queries.
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!jobOffer.id) {
      return;
    }

    setDeclineLoading(true);
    try {
      await decide({
        id: jobOffer.id,
        decision: false,
      });
    } catch {
      // onError already surfaced the toast and invalidated queries.
    } finally {
      setDeclineLoading(false);
    }
  };

  // Acceptance window: the DB status may not have flipped to Expired yet
  // (Inngest is async), so trust the timestamp for immediate feedback.
  const isExpired =
    jobOffer?.status === JobOfferStatus.Expired ||
    Boolean(jobOffer?.expiresAt && new Date(jobOffer.expiresAt) < new Date());

  const canAccept =
    jobOffer?.id &&
    // Must be a member of a provider pool or have a provider user that was shared
    ((jobOffer?.sharedPoolMembers?.length ?? 0) > 0 ||
      (jobOffer?.sharedUsers?.length ?? 0) > 0) &&
    // Make sure the decision wasn't already made
    !jobOffer?.decision?.organizationDecision &&
    // Make sure the job wasn't already accepted by another user
    !jobOffer?.accepted &&
    // Make sure the job wasn't canceled by the organization
    !jobOffer?.canceledByOrganizationBusinessUnitAt &&
    // Make sure the acceptance window hasn't elapsed
    !isExpired;

  // Determine if job is unavailable
  const isJobUnavailable =
    jobOffer?.canceledByOrganizationBusinessUnitAt ||
    Boolean(jobOffer?.accepted && !jobOffer?.decision?.providerDecision);

  // Determine if user has rejected the job offer
  const isRejectedByUser =
    jobOffer?.decision?.providerDecision === false && jobOffer?.decision?.isMe;

  const isAcceptedByMe =
    jobOffer?.decision?.providerDecision === true &&
    jobOffer?.decision?.isMe &&
    jobOffer?.canceledByOrganizationBusinessUnitAt == null &&
    jobOffer?.decision?.providerCanceledAt == null;

  const isAcceptedByUser =
    isAcceptedByMe ||
    // Org view: show accepted when org approved and provider accepted
    (jobOffer?.decision?.providerDecision === true &&
      jobOffer?.decision?.organizationDecision === true &&
      jobOffer?.canceledByOrganizationBusinessUnitAt == null &&
      jobOffer?.decision?.providerCanceledAt == null);

  // Can accept after rejection if job is not accepted, canceled, or expired
  const canAcceptAfterRejection =
    jobOffer?.id &&
    isRejectedByUser &&
    !jobOffer?.decision?.organizationDecision &&
    !isJobUnavailable &&
    !isExpired;

  // Determine card status for styling. The provider's explicit decline takes
  // precedence over an expiry that happened after — "Declined" is the more
  // informative signal in the Inactive list.
  const cardStatus = isJobUnavailable
    ? "unavailable"
    : isAcceptedByUser
      ? "accepted"
      : isRejectedByUser
        ? "rejected"
        : isExpired
          ? "expired"
          : "pending";

  const statusStyles = {
    unavailable: "border-slate-200 bg-white opacity-75",
    accepted: "border-slate-200 bg-white",
    rejected: "border-slate-200 bg-white opacity-75",
    expired: "border-slate-200 bg-white opacity-75",
    pending: "border-slate-200 bg-white",
  };

  const statusBadgeStyles = {
    unavailable: "bg-slate-100 text-slate-600",
    accepted: "bg-emerald-50 text-emerald-700",
    rejected: "bg-slate-100 text-slate-600",
    expired: "bg-slate-100 text-slate-600",
    pending: "bg-primary/10 text-primary",
  };

  return (
    <div
      className={cn(
        "min-w-0 overflow-hidden",
        inDialog
          ? "rounded-xl"
          : "rounded-2xl border shadow-xs transition-shadow hover:shadow-sm",
        !inDialog && statusStyles[cardStatus],
        className,
      )}
    >
      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md rounded-2xl border border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Accept this job?</AlertDialogTitle>
            <AlertDialogDescription>
              By accepting, you agree to the wage terms and schedule for this
              position.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel
              className="mt-0 rounded-xl"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl"
              onClick={handleConfirmAccept}
              disabled={acceptLoading}
            >
              {acceptLoading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : null}
              Confirm & Accept
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          setShowCancelDialog(open);
          if (!open) {
            setCancelOfferNote("");
          }
        }}
      >
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md rounded-2xl border border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this job?</AlertDialogTitle>
            <div className="space-y-2 text-sm">
              <div>
                Canceling with little notice will negatively affect your ability
                to take jobs in the future.
              </div>
              <div className="flex items-center gap-2 text-primary font-medium">
                <MessageSquare className="h-4 w-4" />
                Please consider chatting with the facility before canceling.
              </div>
            </div>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-offer-note" className="text-sm font-medium">
              Reason for canceling <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="cancel-offer-note"
              value={cancelOfferNote}
              onChange={(e) => setCancelOfferNote(e.target.value)}
              rows={3}
              required
            />
            <p className="text-xs text-slate-500">
              The facility will see this reason.
            </p>
          </div>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel
              className="mt-0 rounded-xl"
              onClick={() => {
                setShowCancelDialog(false);
                setCancelOfferNote("");
              }}
            >
              Go Back
            </AlertDialogCancel>
            <Button
              variant="destructive"
              className="rounded-xl"
              onClick={handleConfirmCancel}
              disabled={cancelLoading || cancelOfferNote.trim().length === 0}
            >
              {cancelLoading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <CalendarX2 className="h-5 w-5 mr-1.5" />
              )}
              Confirm Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Shift Confirmation Dialog */}
      <AlertDialog
        open={showCancelShiftDialog}
        onOpenChange={setShowCancelShiftDialog}
      >
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md rounded-2xl border border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this shift?</AlertDialogTitle>
            <AlertDialogDescription>
              The facility will be notified of the cancellation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-shift-note" className="text-sm font-medium">
              Reason for canceling <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="cancel-shift-note"
              value={cancelShiftNote}
              onChange={(e) => setCancelShiftNote(e.target.value)}
              rows={3}
              required
            />
            <p className="text-xs text-slate-500">
              The facility will see this reason.
            </p>
          </div>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel
              className="mt-0 rounded-xl"
              onClick={() => {
                setShowCancelShiftDialog(false);
                setShiftToCancel(null);
                setCancelShiftNote("");
              }}
            >
              Go Back
            </AlertDialogCancel>
            <Button
              variant="destructive"
              className="rounded-xl"
              disabled={cancelShiftNote.trim().length === 0}
              onClick={async () => {
                if (!jobOffer.id || !shiftToCancel) {
                  return;
                }
                await providerCancelShifts({
                  jobOfferId: jobOffer.id,
                  shiftIds: [shiftToCancel],
                  note: cancelShiftNote.trim(),
                });
              }}
            >
              Confirm Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header - Compact version */}
      {compact ? (
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge
                  variant="secondary"
                  className="text-xs font-medium shrink-0 bg-primary/10 text-primary border-0"
                >
                  Pending
                </Badge>
              </div>
              <h3 className="font-semibold text-slate-900 truncate">
                {jobOffer?.facility?.name || "Job Offer"}
              </h3>
              {jobOffer?.organizationBusinessUnit?.name && (
                <p className="text-sm text-slate-600 truncate">
                  {jobOffer.organizationBusinessUnit.name}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Header - Full version */
        <div className="border-b border-slate-200 bg-slate-50/60 px-5 pt-5 pb-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge
              className={cn(
                "shrink-0 border-0 text-xs font-medium",
                statusBadgeStyles[cardStatus],
              )}
            >
              {cardStatus === "accepted"
                ? "Scheduled"
                : cardStatus === "rejected"
                  ? "Declined"
                  : cardStatus === "unavailable"
                    ? "Unavailable"
                    : cardStatus === "expired"
                      ? "Expired"
                      : "Pending"}
            </Badge>
            {jobOffer?.decision?.organizationDecision &&
              jobOffer?.decisionUser && (
                <span className="text-sm text-slate-600">
                  {jobOffer?.decision?.isMe ? (
                    "You accepted"
                  ) : (
                    <>
                      <ProviderNameOpenModal
                        userId={jobOffer.decisionUser.id}
                        firstName={jobOffer.decisionUser.firstName}
                        lastName={jobOffer.decisionUser.lastName}
                      />{" "}
                      accepted
                    </>
                  )}
                </span>
              )}
          </div>
          {/* Primary: Facility name */}
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            {jobOffer?.facility?.name || "Job Offer"}
          </h2>
          {/* Secondary: Business unit name */}
          {jobOffer?.organizationBusinessUnit?.name && (
            <p className="text-base text-slate-700 font-medium mt-0.5">
              {jobOffer.organizationBusinessUnit.name}
            </p>
          )}
          {/* Tertiary: Job title if different from facility */}
          {jobOffer?.title && (
            <p className="text-sm text-slate-500 mt-1">{jobOffer.title}</p>
          )}

          {/* Header action buttons */}
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2 empty:hidden">
            {/* Cancel shift button — provider only */}
            {isAcceptedByMe && !hideActions && (
              <Button
                variant="outline"
                size="sm"
                className="h-10 rounded-xl px-3 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                onClick={handleCancelClick}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <CalendarX2 className="h-4 w-4 mr-1.5" />
                )}
                Cancel Job
              </Button>
            )}
            {/* Reaccept button after rejection */}
            {canAcceptAfterRejection && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 rounded-xl px-3 text-primary hover:bg-primary/5 hover:text-primary"
                onClick={handleAcceptClick}
                disabled={acceptLoading}
              >
                {acceptLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SquarePen className="h-4 w-4" />
                )}
              </Button>
            )}
            {/* Close button for dialog mode */}
            {inDialog && onClose && (
              <Button
                variant="ghost"
                size="sm"
                className="size-10 rounded-xl p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close job details"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      )}

      <div className={cn("space-y-4", compact ? "p-4 pt-3" : "p-5")}>
        {/* Status Messages - hidden in compact mode */}
        {!compact && isRejectedByUser && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-2">
            <X className="h-5 w-5 text-orange-600 shrink-0" />
            <p className="text-orange-700 text-sm">
              You've declined this job offer.
            </p>
          </div>
        )}

        {!compact && jobOffer?.accepted && !jobOffer?.decision && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
            <CalendarX className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-amber-700 text-sm">
              This job is no longer available. Someone else accepted.
            </p>
          </div>
        )}

        {!compact && jobOffer?.canceledByOrganizationBusinessUnitAt && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
            <CalendarX className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-amber-700 text-sm">
              This job was cancelled by the organization.
            </p>
          </div>
        )}

        {!compact &&
          isExpired &&
          !jobOffer?.canceledByOrganizationBusinessUnitAt &&
          !jobOffer?.accepted && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600 shrink-0" />
              <p className="text-amber-700 text-sm">
                The acceptance window for this job offer has expired.
              </p>
            </div>
          )}

        {/* Location/Address */}
        {jobOffer?.facility?.address1 && (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm text-slate-600">
                {formatAddress({
                  address1: jobOffer.facility.address1,
                  address2: jobOffer.facility.address2,
                  city: jobOffer.facility.city,
                  state: jobOffer.facility.state,
                  zipCode: jobOffer.facility.zipCode,
                })}
              </p>
            </div>
          </div>
        )}

        {/* Wage */}
        {jobOffer?.regularWage && (
          <div className="flex items-start gap-3">
            <DollarSign className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-base text-slate-900">
                ${jobOffer.regularWage}
                {wageFrequency}
              </p>
              {!compact &&
                (jobOffer.regularOvertimeWage ||
                  jobOffer.weekendWage ||
                  jobOffer.holidayWage) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 mt-0.5">
                    {jobOffer.regularOvertimeWage && (
                      <span>
                        OT: ${jobOffer.regularOvertimeWage}
                        {wageFrequency}
                      </span>
                    )}
                    {jobOffer.weekendWage && (
                      <span>
                        Weekend: ${jobOffer.weekendWage}
                        {wageFrequency}
                      </span>
                    )}
                    {jobOffer.holidayWage && (
                      <span>
                        Holiday: ${jobOffer.holidayWage}
                        {wageFrequency}
                      </span>
                    )}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Schedule */}
        <ScheduleSummary
          schedules={jobOffer.schedules}
          shifts={jobOffer.shifts}
          compact={compact}
        />

        {/* Pending Org Proposals Banner — provider needs to respond */}
        {!compact &&
          !hideActions &&
          (() => {
            const pendingOrgProposals = jobOffer.proposals?.filter(
              (p) =>
                p.status === "pending" && p.initiatedByRole === "organization",
            );
            if (!pendingOrgProposals?.length) {
              return null;
            }
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">
                    {pendingOrgProposals.length === 1
                      ? "The facility has proposed changes that need your response"
                      : `${pendingOrgProposals.length} facility proposals need your response`}
                  </span>
                </div>
                <div className="space-y-2">
                  {pendingOrgProposals.map((proposal) => (
                    <ProposalListItem
                      key={proposal.id}
                      proposal={proposal}
                      jobOfferId={jobOffer.id!}
                      defaultExpanded
                      role="provider"
                      canRespond
                    />
                  ))}
                </div>
              </div>
            );
          })()}

        {/* Other Proposals */}
        {!compact &&
          !hideActions &&
          (() => {
            const otherProposals = jobOffer.proposals?.filter(
              (p) =>
                !(
                  p.status === "pending" && p.initiatedByRole === "organization"
                ),
            );
            if (!otherProposals?.length) {
              return null;
            }
            return (
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Proposals
                  </span>
                </div>
                <div className="space-y-2">
                  {otherProposals.map((proposal) => (
                    <ProposalListItem
                      key={proposal.id}
                      proposal={proposal}
                      jobOfferId={jobOffer.id!}
                      role="provider"
                      canRespond
                    />
                  ))}
                </div>
              </div>
            );
          })()}

        {/* Per-shift cancel UI for accepted offers — provider only */}
        {!compact &&
          !hideActions &&
          isAcceptedByMe &&
          jobOffer.shifts &&
          jobOffer.shifts.length > 0 && (
            <div className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Manage shifts
                </span>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {[...jobOffer.shifts]
                  .sort((a, b) => {
                    const dateA =
                      typeof a.startsAt === "string"
                        ? new Date(a.startsAt)
                        : a.startsAt;
                    const dateB =
                      typeof b.startsAt === "string"
                        ? new Date(b.startsAt)
                        : b.startsAt;
                    return dateA.getTime() - dateB.getTime();
                  })
                  .map((shift) => {
                    const isCanceled = shift.status === "canceled";
                    const startsAt =
                      typeof shift.startsAt === "string"
                        ? parseISO(shift.startsAt)
                        : shift.startsAt;
                    const endsAt =
                      typeof shift.endsAt === "string"
                        ? parseISO(shift.endsAt)
                        : shift.endsAt;
                    return (
                      <div
                        key={shift.id}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                          isCanceled
                            ? "border-slate-100 bg-slate-50 text-slate-400 line-through"
                            : "border-slate-100 bg-slate-50 text-slate-700"
                        }`}
                      >
                        <span>
                          {format(startsAt, "MMM d, yyyy")} &middot;{" "}
                          {format(startsAt, "h:mm a")} &ndash;{" "}
                          {format(endsAt, "h:mm a")}
                        </span>
                        {!isCanceled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            onClick={() => {
                              setShiftToCancel(shift.id);
                              setShowCancelShiftDialog(true);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isCanceled && (
                          <Badge
                            variant="outline"
                            className="text-xs text-slate-400 border-slate-200"
                          >
                            Canceled
                          </Badge>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

        {/* Description toggle - hidden in compact mode */}
        {!compact && jobOffer?.description && (
          <Button
            variant="outline"
            className="w-full rounded-xl"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Hide description" : "View description"}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>
        )}
      </div>

      {/* Expandable Details Section - hidden in compact mode */}
      {!compact && isExpanded && (
        <div className="px-5 pb-5">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h3 className="font-semibold text-base text-slate-900 mb-2">
              Job Description
            </h3>
            {jobOffer?.description ? (
              <div className="text-sm text-slate-600 prose prose-sm max-w-none">
                <RenderHtml html={jobOffer?.description} />
              </div>
            ) : (
              <p className="text-sm text-slate-400 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                No description provided
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {canAccept && !isRejectedByUser && !hideActions && (
        <div className="flex flex-col gap-3 px-5 pb-5 sm:flex-row">
          <Button
            variant="default"
            className="flex-1 gap-2 font-semibold"
            onClick={handleAcceptClick}
            disabled={acceptLoading || declineLoading || cancelLoading}
          >
            {acceptLoading ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Check className="h-5 w-5 mr-2" />
            )}
            Accept
          </Button>
          <Button
            variant="destructive"
            className="flex-1 gap-2 font-semibold"
            onClick={handleDecline}
            disabled={acceptLoading || declineLoading || cancelLoading}
          >
            {declineLoading ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <X className="h-5 w-5 mr-2" />
            )}
            Decline
          </Button>
        </div>
      )}
    </div>
  );
}
